import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MaskedLines, Reveal } from "@/components/landing/Reveal";
import { useBooking } from "@/context/BookingContext";

const HERO_IMG_DESKTOP = "/umngot-hero.webp";
const HERO_IMG_MOBILE = "/umngot-hero-prev.webp";

export const Hero = () => {
  const { openBooking } = useBooking();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "-14%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} className="relative grid min-h-screen grid-cols-1 md:grid-cols-2 bg-ink grain" data-testid="hero-section">
      {/* Left */}
      <div className="relative z-10 flex flex-col justify-center px-7 py-24 sm:px-12 lg:px-20">
        <Reveal delay={0.1}>
          <p className="eyebrow text-gold mb-8">Premium South India → Northeast Corridor</p>
        </Reveal>

        <h1 className="font-display text-5xl leading-[1.02] text-white sm:text-6xl lg:text-7xl">
          <MaskedLines lines={["Don't Rush the", "Northeast."]} delay={0.25} />
          <MaskedLines lines={["Live It."]} className="italic text-gold" delay={0.55} />
        </h1>

        <Reveal delay={0.9} className="mt-8 max-w-md">
          <p className="text-base leading-relaxed text-white/60 sm:text-lg">
            A thoughtfully curated 6-day luxury escape to Meghalaya for South Indian
            families and professionals who believe a holiday should feel like a holiday —
            not a frantic corporate checklist.
          </p>
        </Reveal>

        <Reveal delay={1.05} className="mt-10 flex flex-col items-start gap-5">
          <Button
            data-testid="explore-journeys-btn"
            onClick={() => openBooking()}
            className="group h-14 rounded-none bg-gold px-8 text-sm font-bold uppercase tracking-[0.12em] text-ink shadow-none transition-all duration-300 hover:-translate-y-0.5 hover:bg-gold-hover"
          >
            Plan My Trip
            <ArrowRight className="ml-1 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Button>
          <button
            data-testid="hero-consult-link"
            onClick={() => openBooking()}
            className="flex items-center gap-2 text-xs font-medium text-white/50 transition-colors hover:text-gold"
          >
            <Lock className="h-3.5 w-3.5" />
            Private 1-to-1 Travel Consultations Scheduled Around Your Corporate Calendar
          </button>
        </Reveal>
      </div>

      {/* Right image */}
      <div className="relative min-h-[60vh] overflow-hidden sm:min-h-[72vh] md:min-h-screen">
        <div className="absolute inset-4 overflow-hidden rounded-2xl sm:inset-6 md:inset-8 lg:inset-10">
          <motion.img
            src={HERO_IMG_MOBILE}
            alt="Traditional wooden boats on the crystal-clear emerald waters of the Umngot River"
            style={{ y, scale }}
            className="absolute left-0 top-0 h-[118%] w-full object-cover object-center lg:hidden"
            data-testid="hero-image"
          />
          <motion.img
            src={HERO_IMG_DESKTOP}
            alt="Traditional wooden boat on the crystal-clear emerald waters of the Umngot River"
            style={{ y, scale }}
            className="absolute left-0 top-0 hidden h-[118%] w-full object-cover object-center lg:block"
            data-testid="hero-image-desktop"
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/50 via-transparent to-transparent" />
          <div className="absolute bottom-5 left-5 z-10">
            <p className="eyebrow text-white/80">Umngot River · Shnongpdeng</p>
          </div>
        </div>
      </div>
    </section>
  );
};
